import { ALL_VERTICALS, GLOBAL_COUNTRY } from "@app/shared"
import { useQuery } from "@tanstack/react-query"
import { type Country, countriesQueryKeys, listCountries } from "../api/countries"
import { type Vertical, listVerticals, registriesQueryKeys } from "../api/registries"
import { useScope } from "./scope-context"

export interface ScopeLabels {
	/** Display name for the active country, or "all countries" when global. */
	readonly countryLabel: string
	/** Display name for the active vertical, or "consolidated" when unset. */
	readonly verticalLabel: string
}

/**
 * Resolve human display names for the active scope so the dashboard's
 * "LIVE —" line can read "Benin · Cashew" rather than "BJ · cashew-uuid".
 *
 * Reads the live countries/verticals registries (react-query dedupes these
 * with the topbar {@link ScopeSelector}'s identical queries). Falls back to
 * the raw code/id while the lists load or on a miss, and to the friendly
 * sentinels ("all countries" / "consolidated") for the unfiltered dimensions
 * — the fully-global "Ajman Free Zone" default is composed by the caller via
 * `isGlobal`. Errors degrade to an empty list (no labels), never throw.
 */
export function useScopeLabels(): ScopeLabels {
	const { country, vertical } = useScope()

	const countriesQuery = useQuery({
		queryKey: countriesQueryKeys.all,
		queryFn: () => listCountries().catch(() => [] as Country[]),
	})
	const verticalsQuery = useQuery({
		queryKey: registriesQueryKeys.verticals(),
		queryFn: () => listVerticals().catch(() => [] as Vertical[]),
	})

	const countries = countriesQuery.data ?? []
	const verticals = verticalsQuery.data ?? []

	const countryLabel =
		country === GLOBAL_COUNTRY
			? "all countries"
			: (countries.find((c) => c.code === country)?.name ?? country)
	const verticalLabel =
		vertical === ALL_VERTICALS
			? "consolidated"
			: (verticals.find((v) => v.id === vertical)?.name ?? vertical)

	return { countryLabel, verticalLabel }
}
